/**
 * Browser-safe tier + experiment price resolution (no dotenv / Node deps).
 * Shared by helper.js (server) and Marko client components.
 */

const EXP_DISPLAY_BASE_FIELDS = {
	monthly: 'price',
	yearly: 'yearly_price',
	annual: 'price_annual',
	slash: 'slash_price',
	pathMonthly: 'path_price',
	pathYearly: 'yearly_path_price',
	pathAnnual: 'path_price_annual',
	daily: 'price_daily',
	yearlyDaily: 'yearly_price_daily',
	pathDaily: 'path_price_daily',
	pathSlashDaily: 'path_slash_price_daily',
	yearlyPathDaily: 'yearly_path_price_daily',
	yearlyPathSlash: 'yearly_path_slash_price',
	pathSlash: 'path_slash_price',
};

const EXP_USD_PRICE_ID_FIELDS = {
	op_monthly: 'op_monthly_price_id',
	op_yearly: 'op_yearly_price_id',
	pk_monthly: 'pk_monthly_price_id',
	pk_yearly: 'pk_yearly_price_id',
};

const EXP_OTB_DISPLAY_FIELDS = {
	likeMonthly: 'addons-like-price-monthly',
	likeYearly: 'addons-like-price-yearly',
	storyMonthly: 'addons-story-price-monthly',
	storyYearly: 'addons-story-price-yearly',
	engagementMonthly: 'addons-engagement-price-monthly',
	engagementYearly: 'addons-engagement-price-yearly',
};

const EXP_OTB_USD_PRICE_ID_FIELDS = {
	op_like_monthly: 'op-addons-like-monthly',
	op_like_yearly: 'op-addons-like-yearly',
	op_story_monthly: 'op-addons-story-monthly',
	op_story_yearly: 'op-addons-story-yearly',
	op_engagement_monthly: 'op-addons-engagement-monthly',
	op_engagement_yearly: 'op-addons-engagement-yearly',
	pk_like_monthly: 'pk-addons-like-monthly',
	pk_like_yearly: 'pk-addons-like-yearly',
	pk_story_monthly: 'pk-addons-story-monthly',
	pk_story_yearly: 'pk-addons-story-yearly',
	pk_engagement_monthly: 'pk-addons-engagement-monthly',
	pk_engagement_yearly: 'pk-addons-engagement-yearly',
};

function expPrefixedFieldKeys(baseFields, prefix) {
	const out = {};
	for (const [alias, field] of Object.entries(baseFields)) {
		out[alias] = prefix ? `${prefix}${field}` : field;
	}
	return out;
}

function buildExpPlusVariantKeys(variantSlug) {
	const prefix = `tier1_${variantSlug}_`;
	return {
		usesExisting: false,
		display: expPrefixedFieldKeys(EXP_DISPLAY_BASE_FIELDS, prefix),
		usdPriceIds: expPrefixedFieldKeys(EXP_USD_PRICE_ID_FIELDS, prefix),
		otbDisplay: expPrefixedFieldKeys(EXP_OTB_DISPLAY_FIELDS, prefix),
		otbUsdPriceIds: expPrefixedFieldKeys(EXP_OTB_USD_PRICE_ID_FIELDS, prefix),
	};
}

export const pricingExperimentPlanKeys = {
	t1_control: {
		usesExisting: true,
		display: expPrefixedFieldKeys(EXP_DISPLAY_BASE_FIELDS, ''),
		usdPriceIds: { ...EXP_USD_PRICE_ID_FIELDS },
		otbDisplay: { ...EXP_OTB_DISPLAY_FIELDS },
		otbUsdPriceIds: { ...EXP_OTB_USD_PRICE_ID_FIELDS },
	},
	t2_control: {
		usesExisting: true,
		display: expPrefixedFieldKeys(EXP_DISPLAY_BASE_FIELDS, 'tier2_'),
		usdPriceIds: expPrefixedFieldKeys(EXP_USD_PRICE_ID_FIELDS, 'tier2_'),
		otbDisplay: expPrefixedFieldKeys(EXP_OTB_DISPLAY_FIELDS, 'tier2_'),
		otbUsdPriceIds: expPrefixedFieldKeys(EXP_OTB_USD_PRICE_ID_FIELDS, 'tier2_'),
	},
	t3_control: {
		usesExisting: true,
		display: expPrefixedFieldKeys(EXP_DISPLAY_BASE_FIELDS, 'tier3_'),
		usdPriceIds: expPrefixedFieldKeys(EXP_USD_PRICE_ID_FIELDS, 'tier3_'),
		otbDisplay: expPrefixedFieldKeys(EXP_OTB_DISPLAY_FIELDS, 'tier3_'),
		otbUsdPriceIds: expPrefixedFieldKeys(EXP_OTB_USD_PRICE_ID_FIELDS, 'tier3_'),
	},
	t2_tier1_price: {
		usesExisting: true,
		display: expPrefixedFieldKeys(EXP_DISPLAY_BASE_FIELDS, ''),
		usdPriceIds: { ...EXP_USD_PRICE_ID_FIELDS },
		otbDisplay: { ...EXP_OTB_DISPLAY_FIELDS },
		otbUsdPriceIds: { ...EXP_OTB_USD_PRICE_ID_FIELDS },
	},
	t3_tier1_price: {
		usesExisting: true,
		display: expPrefixedFieldKeys(EXP_DISPLAY_BASE_FIELDS, ''),
		usdPriceIds: { ...EXP_USD_PRICE_ID_FIELDS },
		otbDisplay: { ...EXP_OTB_DISPLAY_FIELDS },
		otbUsdPriceIds: { ...EXP_OTB_USD_PRICE_ID_FIELDS },
	},
	t1_plus_10: buildExpPlusVariantKeys('plus_10'),
	t1_plus_20: buildExpPlusVariantKeys('plus_20'),
	t1_plus_50: buildExpPlusVariantKeys('plus_50'),
};

export const pricingExperimentCurrencies = {
	t1: ['USD', 'GBP', 'DKK', 'EUR', 'CZK', 'CHF'],
	t2: ['RUB'],
	t3: [
		'ARS', 'NGN', 'KES', 'ZAR', 'PYG', 'INR',
		'PHP', 'CLP', 'GTQ', 'PAB', 'PEN', 'COP', 'UYU', 'DZD', 'MXN', 'BRL',
	],
};

export function getPricingExperimentPlanKeys(priceSetKey) {
	return pricingExperimentPlanKeys[priceSetKey] || pricingExperimentPlanKeys.t1_control;
}

export function getPricingExperimentDisplayKey(priceSetKey, field = 'monthly') {
	const keys = getPricingExperimentPlanKeys(priceSetKey);
	return keys.display[field] || keys.display.monthly;
}

export function getPricingExperimentUsdPriceIdKey(priceSetKey, field = 'op_monthly') {
	const keys = getPricingExperimentPlanKeys(priceSetKey);
	return keys.usdPriceIds[field] || null;
}

export function getPricingExperimentOtbDisplayKey(priceSetKey, field = 'likeMonthly') {
	const keys = getPricingExperimentPlanKeys(priceSetKey);
	return keys.otbDisplay?.[field] || null;
}

export function getPricingExperimentOtbUsdPriceIdKey(priceSetKey, field = 'op_like_monthly') {
	const keys = getPricingExperimentPlanKeys(priceSetKey);
	return keys.otbUsdPriceIds?.[field] || null;
}

function findExperimentFieldAlias(baseFields, priceKey) {
	const cleanKey = String(priceKey).replace(/^tier[23]_/, '');
	for (const [alias, field] of Object.entries(baseFields)) {
		if (field === cleanKey) return alias;
	}
	return null;
}

function isOtbPriceKey(priceKey) {
	const key = String(priceKey || '');
	return key.startsWith('addons-');
}

export function resolveExperimentPriceField(priceSetKey, otbSetKey, priceKey) {
	if (!priceSetKey || !priceKey) return null;
	const cleanKey = String(priceKey).replace(/^tier[23]_/, '');

	if (isOtbPriceKey(cleanKey)) {
		const setKey = otbSetKey || priceSetKey;
		const alias = findExperimentFieldAlias(EXP_OTB_DISPLAY_FIELDS, cleanKey);
		if (alias) return getPricingExperimentOtbDisplayKey(setKey, alias);
		return cleanKey;
	}

	const alias = findExperimentFieldAlias(EXP_DISPLAY_BASE_FIELDS, cleanKey);
	if (alias) return getPricingExperimentDisplayKey(priceSetKey, alias);
	return cleanKey;
}

const EXP_PLUS_PRICE_SET_KEYS = new Set(['t1_plus_10', 't1_plus_20', 't1_plus_50']);

/**
 * Base display fields mapped to the values of the active tier1 plus variant.
 * Returns {} for every other price set, and never adds a field the control plan does not
 * already price, so control / tier2 / tier3 checkouts keep the prices they show today.
 * Values are strings to match the plan objects checkout pages inline into client scripts.
 */
export function buildPlusVariantPlanPriceOverrides(planData, pricingExperiment = null) {
	const priceSetKey = pricingExperiment?.active ? pricingExperiment.priceSetKey : null;
	if (!planData || !priceSetKey || !EXP_PLUS_PRICE_SET_KEYS.has(priceSetKey)) return {};

	const variantFields = getPricingExperimentPlanKeys(priceSetKey).display;
	const overrides = {};
	for (const [alias, baseField] of Object.entries(EXP_DISPLAY_BASE_FIELDS)) {
		if (planData[baseField] == null || planData[baseField] === '') continue;
		const variantValue = planData[variantFields[alias]];
		if (variantValue == null || variantValue === '') continue;
		overrides[baseField] = String(variantValue);
	}
	return overrides;
}

/** Plus variant overrides per plan for one currency; plans without overrides are omitted. */
export function buildPlusVariantPlansPriceOverrides(
	plans,
	currencyCode,
	pricingExperiment = null,
	planNames = ['basic', 'pro', 'experts'],
) {
	const currencyPlans = plans?.[currencyCode] || plans?.USD || {};
	const overridesByPlan = {};
	for (const planName of planNames) {
		const overrides = buildPlusVariantPlanPriceOverrides(currencyPlans[planName], pricingExperiment);
		if (Object.keys(overrides).length) overridesByPlan[planName] = overrides;
	}
	return overridesByPlan;
}

/**
 * Price sets whose display fields are the plain tier-1 keys, so geo tiering must be skipped.
 * Plus variants are tier-1 based too: a missing tier1_plus_* field falls back to tier-1,
 * never to a tier2/tier3 geo price.
 */
const TIER1_BASE_KEY_PRICE_SETS = ['t1_control', 't2_tier1_price', 't3_tier1_price'];

function usesExperimentTier1BaseKeys(priceSetKey) {
	if (!priceSetKey) return false;
	return TIER1_BASE_KEY_PRICE_SETS.includes(priceSetKey)
		|| EXP_PLUS_PRICE_SET_KEYS.has(priceSetKey);
}

function priceFromStoredTier(planData, cleanKey, priceKey, tieredPricing) {
	const tier = String(tieredPricing || '').trim().toLowerCase();
	if (tier.startsWith('tier1plus_')) {
		const plusSlug = tier.replace('tier1plus_', 'plus_');
		const plusKey = `tier1_${plusSlug}_${cleanKey}`;
		return planData[plusKey] ?? planData[cleanKey] ?? planData[priceKey];
	}
	if (tier === 'tier1') {
		return planData[cleanKey] ?? planData[priceKey];
	}
	if (tier === 'tier2') {
		return planData['tier2_' + cleanKey] ?? planData[cleanKey] ?? planData[priceKey];
	}
	if (tier === 'tier3') {
		return planData['tier3_' + cleanKey] ?? planData[cleanKey] ?? planData[priceKey];
	}
	return null;
}

export const TIER2_CURRENCIES = ['RUB', 'BRL'];

export const TIER3_CURRENCIES = [
	'ARS', 'PHP', 'CLP', 'COP', 'MXN', 'INR', 'ZAR',
];

export function isTiered2Currency(currencyCode) {
	return TIER2_CURRENCIES.includes(currencyCode);
}

export function isTiered3Currency(currencyCode) {
	return TIER3_CURRENCIES.includes(currencyCode);
}

const CONTROL_ONLY_ADDON_KEYS = new Set(['autopilot', 'aimatch']);

function isControlOnlyAddonPriceKey(priceKey) {
	const key = String(priceKey || '').replace(/^tier[23]_/, '');
	return CONTROL_ONLY_ADDON_KEYS.has(key);
}

function tiktokPlanPrice(planData, priceKey) {
	const key = String(priceKey || '');
	if (key.includes('tiktok')) return planData[key];
	const ttKey = `tiktok_${key.replace(/^tier[23]_/, '')}`;
	return planData[ttKey] ?? planData[key];
}

export function getTierPrice(planData, priceKey, currencyCode, pricingExperiment = null, options = {}) {
	if (!planData || priceKey == null) return undefined;

	const { skipTiering = false, skipExperiment = false, isTiktok = false } = options;
	const cleanKey = String(priceKey).replace(/^tier[23]_/, '');

	if (isTiktok || String(priceKey).includes('tiktok')) {
		return tiktokPlanPrice(planData, priceKey);
	}

	if (isControlOnlyAddonPriceKey(cleanKey)) {
		return planData[cleanKey] ?? planData[priceKey];
	}

	const storedPlus = !!(
		pricingExperiment?.fromStoredTier
		&& String(pricingExperiment.tieredPricing || '').toLowerCase().startsWith('tier1plus_')
	);

	// Purchased PostHog plus_* must not inherit t1_control / Lab yearly_price.
	if (storedPlus) {
		const storedPrice = priceFromStoredTier(planData, cleanKey, priceKey, pricingExperiment.tieredPricing);
		if (storedPrice != null) return storedPrice;
	}

	if (pricingExperiment?.fromStoredTier && pricingExperiment?.tieredPricing) {
		const storedTierPrice = priceFromStoredTier(planData, cleanKey, priceKey, pricingExperiment.tieredPricing);
		if (storedTierPrice != null) return storedTierPrice;
	}

	if (!skipExperiment && pricingExperiment?.active && pricingExperiment.priceSetKey) {
		const expField = resolveExperimentPriceField(
			pricingExperiment.priceSetKey,
			pricingExperiment.otbSetKey,
			priceKey,
		);
		if (expField && planData[expField] != null && planData[expField] !== '') {
			return planData[expField];
		}
	}

	if (
		pricingExperiment?.pricingLabActive
		&& !storedPlus
		&& !EXP_PLUS_PRICE_SET_KEYS.has(pricingExperiment?.priceSetKey)
	) {
		return planData[cleanKey] ?? planData[priceKey];
	}

	if (skipTiering || (pricingExperiment?.active && usesExperimentTier1BaseKeys(pricingExperiment.priceSetKey))) {
		return planData[cleanKey] ?? planData[priceKey];
	}

	if (isTiered3Currency(currencyCode)) {
		return planData['tier3_' + cleanKey] ?? planData[cleanKey] ?? planData[priceKey];
	}
	if (isTiered2Currency(currencyCode)) {
		return planData['tier2_' + cleanKey] ?? planData[cleanKey] ?? planData[priceKey];
	}
	return planData[cleanKey] ?? planData[priceKey];
}

const DEFAULT_UPGRADE_FEATURES = {
	basic: ['Custom Audience Targeting', 'AI-Powered Growth', 'Live Analytics', 'Growth Insights'],
	pro: ['Everything in the Basic plan', 'Turbocharged AI-Powered Growth', 'Advanced Audience Targeting', 'Plixi Cloud™'],
	experts: ['Everything in the Pro plan', 'Turbocharged AI-Powered Growth', 'Advanced Audience Targeting', 'Plixi Cloud™'],
};

/** Build one plan row for upgrade modals (browser + server safe). */
export function buildUpgradePlanRow(plans, planName, currencyCode, pricingExperiment, isTiktok, features) {
	const planData = plans?.[currencyCode]?.[planName];
	if (!planData) return null;
	const priceOf = (priceKey) => {
		if (isTiktok || String(priceKey).includes('tiktok')) {
			return planData[priceKey] != null
				? planData[priceKey]
				: (planData[String(priceKey).replace('tiktok_', '')] ?? planData[priceKey]);
		}
		return getTierPrice(planData, priceKey, currencyCode, pricingExperiment);
	};
	return {
		name: planName,
		price: priceOf(isTiktok ? 'tiktok_price' : 'price'),
		tiktok_price: priceOf('tiktok_price'),
		price_annual: priceOf(isTiktok ? 'tiktok_price_annual' : 'price_annual'),
		tiktok_price_annual: priceOf('tiktok_price_annual'),
		tiktok_yearly_price: priceOf('tiktok_yearly_price'),
		yearly_price: priceOf(isTiktok ? 'tiktok_yearly_price' : 'yearly_price'),
		features: features || DEFAULT_UPGRADE_FEATURES[planName] || [],
	};
}

/** Build basic/pro/experts map for upgrade modals. */
export function buildUpgradePlansMap(plans, currencyCode, pricingExperiment, isTiktok) {
	const code = currencyCode || 'USD';
	const safePlans = plans || {};
	return {
		basic: buildUpgradePlanRow(safePlans, 'basic', code, pricingExperiment, isTiktok, DEFAULT_UPGRADE_FEATURES.basic),
		pro: buildUpgradePlanRow(safePlans, 'pro', code, pricingExperiment, isTiktok, DEFAULT_UPGRADE_FEATURES.pro),
		experts: buildUpgradePlanRow(safePlans, 'experts', code, pricingExperiment, isTiktok, DEFAULT_UPGRADE_FEATURES.experts),
	};
}

/** Field lookup tables the browser needs to mirror getTierPrice exactly. */
function buildTierPricingClientTables() {
	const displayFieldAliases = {};
	for (const [alias, field] of Object.entries(EXP_DISPLAY_BASE_FIELDS)) {
		displayFieldAliases[field] = alias;
	}
	const otbFieldAliases = {};
	for (const [alias, field] of Object.entries(EXP_OTB_DISPLAY_FIELDS)) {
		otbFieldAliases[field] = alias;
	}
	const priceSets = {};
	for (const [priceSetKey, keys] of Object.entries(pricingExperimentPlanKeys)) {
		priceSets[priceSetKey] = { display: keys.display, otbDisplay: keys.otbDisplay };
	}
	return {
		displayFieldAliases,
		otbFieldAliases,
		priceSets,
		tier1BaseKeyPriceSets: [...TIER1_BASE_KEY_PRICE_SETS, ...EXP_PLUS_PRICE_SET_KEYS],
		controlOnlyAddonKeys: [...CONTROL_ONLY_ADDON_KEYS],
		tier2Currencies: TIER2_CURRENCIES,
		tier3Currencies: TIER3_CURRENCIES,
	};
}

/**
 * Inline client script exposing window.plixiTierPricing — the browser mirror of getTierPrice.
 * Pages inline this instead of copy-pasting the experiment field mapping, so display prices
 * cannot drift from the server (and therefore from the amount actually charged).
 */
export function buildTierPricingClientScript() {
	return `(function () {
	var T = ${JSON.stringify(buildTierPricingClientTables())};

	function cleanPriceKey(priceKey) {
		return String(priceKey).replace(/^tier[23]_/, '');
	}

	function firstDefined(planData, primaryKey, fallbackKey) {
		var value = planData[primaryKey];
		if (value !== undefined && value !== null) return value;
		return planData[fallbackKey];
	}

	function isTiered2Currency(currencyCode) {
		return T.tier2Currencies.indexOf(currencyCode) !== -1;
	}

	function isTiered3Currency(currencyCode) {
		return T.tier3Currencies.indexOf(currencyCode) !== -1;
	}

	function priceSetFields(priceSetKey) {
		return T.priceSets[priceSetKey] || T.priceSets.t1_control;
	}

	function experimentPriceField(priceSetKey, otbSetKey, priceKey) {
		if (!priceSetKey || priceKey === null || priceKey === undefined) return null;
		var cleanKey = cleanPriceKey(priceKey);

		if (cleanKey.indexOf('addons-') === 0) {
			var otbAlias = T.otbFieldAliases[cleanKey];
			if (!otbAlias) return cleanKey;
			var otbFields = priceSetFields(otbSetKey || priceSetKey).otbDisplay || {};
			return otbFields[otbAlias] || null;
		}

		var alias = T.displayFieldAliases[cleanKey];
		if (!alias) return cleanKey;
		var fields = priceSetFields(priceSetKey).display || {};
		return fields[alias] || fields.monthly;
	}

	function usesExperimentTier1BaseKeys(priceSetKey) {
		if (!priceSetKey) return false;
		return T.tier1BaseKeyPriceSets.indexOf(priceSetKey) !== -1;
	}

	function isControlOnlyAddonPriceKey(priceKey) {
		return T.controlOnlyAddonKeys.indexOf(cleanPriceKey(priceKey)) !== -1;
	}

	function priceFromStoredTier(planData, cleanKey, priceKey, tieredPricing) {
		var tier = String(tieredPricing || '').trim().toLowerCase();
		if (tier.indexOf('tier1plus_') === 0) {
			var plusKey = 'tier1_' + tier.replace('tier1plus_', 'plus_') + '_' + cleanKey;
			var plusValue = planData[plusKey];
			if (plusValue !== undefined && plusValue !== null) return plusValue;
			return firstDefined(planData, cleanKey, priceKey);
		}
		if (tier === 'tier1') return firstDefined(planData, cleanKey, priceKey);
		if (tier === 'tier2' || tier === 'tier3') {
			var tierValue = planData[tier + '_' + cleanKey];
			if (tierValue !== undefined && tierValue !== null) return tierValue;
			return firstDefined(planData, cleanKey, priceKey);
		}
		return null;
	}

	function resolveTierPrice(planData, priceKey, currencyCode, options) {
		options = options || {};
		if (!planData || priceKey === null || priceKey === undefined) return undefined;

		if (options.isTiktok || String(priceKey).indexOf('tiktok') !== -1) {
			if (String(priceKey).indexOf('tiktok') !== -1) return planData[priceKey];
			var ttKey = 'tiktok_' + cleanPriceKey(priceKey);
			if (planData[ttKey] !== undefined && planData[ttKey] !== null) return planData[ttKey];
			return planData[priceKey];
		}

		var experiment = options.experiment !== undefined
			? options.experiment
			: window.plixiPricingExperiment;
		var cleanKey = cleanPriceKey(priceKey);

		if (isControlOnlyAddonPriceKey(cleanKey)) {
			return firstDefined(planData, cleanKey, priceKey);
		}

		var storedPlus = !!(
			experiment
			&& experiment.fromStoredTier
			&& String(experiment.tieredPricing || '').toLowerCase().indexOf('tier1plus_') === 0
		);
		if (storedPlus) {
			var plusStoredPrice = priceFromStoredTier(planData, cleanKey, priceKey, experiment.tieredPricing);
			if (plusStoredPrice !== undefined && plusStoredPrice !== null) return plusStoredPrice;
		}

		if (!options.skipExperiment && experiment && experiment.active && experiment.priceSetKey) {
			var expField = experimentPriceField(experiment.priceSetKey, experiment.otbSetKey, priceKey);
			if (expField && planData[expField] !== undefined && planData[expField] !== null && planData[expField] !== '') {
				return planData[expField];
			}
		}

		if (experiment && experiment.fromStoredTier && experiment.tieredPricing) {
			var storedTierPrice = priceFromStoredTier(planData, cleanKey, priceKey, experiment.tieredPricing);
			if (storedTierPrice !== undefined && storedTierPrice !== null) return storedTierPrice;
		}

		if (
			experiment
			&& experiment.pricingLabActive
			&& !storedPlus
			&& !(experiment.priceSetKey && experiment.priceSetKey.indexOf('t1_plus_') === 0)
		) {
			return firstDefined(planData, cleanKey, priceKey);
		}

		if (options.skipTiering || (experiment && experiment.active && usesExperimentTier1BaseKeys(experiment.priceSetKey))) {
			return firstDefined(planData, cleanKey, priceKey);
		}

		if (isTiered3Currency(currencyCode)) {
			var tier3Value = planData['tier3_' + cleanKey];
			if (tier3Value !== undefined && tier3Value !== null) return tier3Value;
			return firstDefined(planData, cleanKey, priceKey);
		}
		if (isTiered2Currency(currencyCode)) {
			var tier2Value = planData['tier2_' + cleanKey];
			if (tier2Value !== undefined && tier2Value !== null) return tier2Value;
			return firstDefined(planData, cleanKey, priceKey);
		}
		return firstDefined(planData, cleanKey, priceKey);
	}

	window.plixiTierPricing = {
		experimentPriceField: experimentPriceField,
		usesExperimentTier1BaseKeys: usesExperimentTier1BaseKeys,
		isTiered2Currency: isTiered2Currency,
		isTiered3Currency: isTiered3Currency,
		resolveTierPrice: resolveTierPrice
	};
	window.isTiered2Currency = window.isTiered2Currency || isTiered2Currency;
	window.isTiered3Currency = window.isTiered3Currency || isTiered3Currency;
})();`;
}
